import { z } from 'zod'

/**
 * AI Ark, the part every side needs (docs/AI-ARK-HANDOVER.md section 4).
 *
 * The contract the six streams build against: the filter model the screen
 * edits, every /api/v1/ai-ark route's request and answer, the export run and
 * job states, the error codes, and the `ai_ark` settings row with its
 * defaults. The web app and the API both import it, so it holds types and zod
 * schemas only and no runtime dependency beyond zod.
 *
 * Who fills what:
 * - S1 (client and money) the real client behind `AiArkClient` in
 *   packages/intel/src/ai-ark/, the cost estimate, the budget check, the
 *   ledger and GET /ai-ark/credits.
 * - S2 (search) filters.ts (this filter model to the API body and back, and
 *   the pasted payload parser), vocab, preview, company search, estimate,
 *   saved searches.
 * - S3 (exports) runs and jobs, the worker, the sweeper, the webhook, the import.
 * - S4 and S5 the screens, S6 the campaign hand-over.
 *
 * Prices, limits and rates are never written in code: they come from the
 * settings row (`aiArkSettingsSchema`), seeded by migration 0220 with exactly
 * `AI_ARK_SETTINGS_DEFAULT`.
 */

/* ------------------------------------------------------------------ */
/* Vocabulary                                                          */
/* ------------------------------------------------------------------ */

/** How a text filter matches: SMART also finds related wording, WORD is the phrase with words around it, STRICT is exact. */
/**
 * AI Ark is put away (owner, 2026-11-04: "hide the AI ARK option from the
 * sidebar, keep all the code there just hide the sidebar and disable all it's
 * endpoints that can be called through the api").
 *
 * While this is true every route under `/api/v1/ai-ark` answers 404 before any
 * of its code runs, AI Ark's webhook included, and the console leaves it out:
 * the Discovery menu, the Settings tab, the command palette, and its pages send a
 * bookmark back to Discovery. The code, the tables, the settings row and the
 * people it already brought into Contacts all stay. Bringing it back is setting
 * this to false and deploying.
 */
export const AI_ARK_PUT_AWAY = true

/** What a call to a put-away AI Ark route is told. */
export const AI_ARK_PUT_AWAY_MESSAGE = 'AI Ark is switched off on this platform.'

/** Whether a request path is one of AI Ark's routes, which `AI_ARK_PUT_AWAY` closes. */
export function isAiArkApiPath(path: string): boolean {
  const bare = (path.split('?')[0] ?? '').replace(/\/{2,}/g, '/').toLowerCase()
  return bare === '/api/v1/ai-ark' || bare.startsWith('/api/v1/ai-ark/')
}

export const AI_ARK_MATCH_MODES = ['SMART', 'WORD', 'STRICT'] as const
export type AiArkMatchMode = (typeof AI_ARK_MATCH_MODES)[number]

export const AI_ARK_SENIORITIES = [
  'founder',
  'owner',
  'partner',
  'c_suite',
  'vp',
  'director',
  'head',
  'manager',
  'senior',
  'mid_level',
  'entry',
  'intern',
] as const
export type AiArkSeniority = (typeof AI_ARK_SENIORITIES)[number]

export const AI_ARK_COMPANY_TYPES = [
  'PRIVATELY_HELD',
  'SELF_OWNED',
  'SELF_EMPLOYED',
  'PARTNERSHIP',
  'PUBLIC_COMPANY',
  'NON_PROFIT',
  'EDUCATIONAL',
  'GOVERNMENT_AGENCY',
] as const
export type AiArkCompanyType = (typeof AI_ARK_COMPANY_TYPES)[number]

export const AI_ARK_SOCIAL_NETWORKS = ['FACEBOOK', 'INSTAGRAM', 'TWITTER', 'LINKEDIN'] as const
export type AiArkSocialNetwork = (typeof AI_ARK_SOCIAL_NETWORKS)[number]

export const AI_ARK_PROFILE_BADGES = ['VERIFIED', 'PREMIUM', 'OPEN_TO_WORK', 'INFLUENCER', 'CREATOR', 'HIRING'] as const
export type AiArkProfileBadge = (typeof AI_ARK_PROFILE_BADGES)[number]

/** Where an account keyword is looked for. */
export const AI_ARK_ACCOUNT_KEYWORD_SOURCES = ['NAME', 'KEYWORD', 'SEO', 'DESCRIPTION', 'INDUSTRY'] as const
export type AiArkAccountKeywordSource = (typeof AI_ARK_ACCOUNT_KEYWORD_SOURCES)[number]

/** Where a person keyword is looked for (at most 5 at once, the API's limit). */
export const AI_ARK_CONTACT_KEYWORD_SOURCES = [
  'HEADLINE',
  'SUMMARY',
  'SKILL',
  'CERTIFICATION',
  'COURSE',
  'PROJECTS',
  'PUBLICATION',
  'PATENT',
  'AWARD',
  'ORGANIZATION',
  'VOLUNTEERING',
  'TEST_SCORE',
  'WORK_HISTORY_DESCRIPTION',
  'EDUCATION_DESCRIPTION',
  'LANGUAGE_SKILL',
] as const
export type AiArkContactKeywordSource = (typeof AI_ARK_CONTACT_KEYWORD_SOURCES)[number]

/** Headcount growth windows for `account.metric.growth`. */
export const AI_ARK_GROWTH_TIMEFRAMES = ['ONE', 'THREE', 'SIX', 'TWELVE', 'TWENTY_FOUR'] as const
export type AiArkGrowthTimeframe = (typeof AI_ARK_GROWTH_TIMEFRAMES)[number]

/** The vendored vocabularies served by GET /ai-ark/vocab/:kind (packages/intel/src/ai-ark/vocab/). */
export const AI_ARK_VOCAB_KINDS = ['industries', 'technologies', 'departments-and-functions'] as const
export type AiArkVocabKind = (typeof AI_ARK_VOCAB_KINDS)[number]

/** The two searches the screen switches between. */
export const AI_ARK_SEARCH_KINDS = ['people', 'company'] as const
export type AiArkSearchKind = (typeof AI_ARK_SEARCH_KINDS)[number]

/* ------------------------------------------------------------------ */
/* The filter model (what the screen edits)                            */
/* ------------------------------------------------------------------ */

/**
 * The building blocks. Every list is optional so an empty group is simply
 * absent, and filters.ts drops empty groups before it builds the API body.
 * `include` is the API's `any.include` (OR), `exclude` is `any.exclude`, and
 * `includeAll` is `all.include` (AND). The API refuses a filter made of
 * excludes alone, and filters.ts reports that as `ai_ark_filter_invalid`.
 */
const values = z.array(z.string().min(1)).max(300)

export const aiArkListValuesSchema = z.object({
  include: values.optional(),
  exclude: values.optional(),
  includeAll: values.optional(),
})
export type AiArkListValues = z.infer<typeof aiArkListValuesSchema>

export const aiArkTextValuesSchema = aiArkListValuesSchema.extend({
  mode: z.enum(AI_ARK_MATCH_MODES).default('SMART'),
})
export type AiArkTextValues = z.infer<typeof aiArkTextValuesSchema>

/** One band of a range filter. Leave out `max` for "min and over", `min` for "under max". */
export const aiArkRangeBandSchema = z.object({ min: z.number().optional(), max: z.number().optional() })
export type AiArkRangeBand = z.infer<typeof aiArkRangeBandSchema>

/** Bands OR'd together. `presence` is the API's `ALL` (has a value) or `NONE` (has none), for founded year and revenue. */
export const aiArkRangeSchema = z.object({
  bands: z.array(aiArkRangeBandSchema).max(20).default([]),
  presence: z.enum(['ALL', 'NONE']).optional(),
})
export type AiArkRange = z.infer<typeof aiArkRangeSchema>

/** A company picked from Company Search, so the screen can show its name next to the id the API takes. */
export const aiArkCompanyRefSchema = z.object({ id: z.string().min(1), name: z.string() })
export type AiArkCompanyRef = z.infer<typeof aiArkCompanyRefSchema>

export const aiArkCompanyRefsSchema = z.object({
  include: z.array(aiArkCompanyRefSchema).max(300).optional(),
  exclude: z.array(aiArkCompanyRefSchema).max(300).optional(),
})
export type AiArkCompanyRefs = z.infer<typeof aiArkCompanyRefsSchema>

/** Months as a min and max, the screen's form of the API's `{ year, month }` pairs. */
export const aiArkMonthsSchema = z.object({ min: z.number().int().min(0).optional(), max: z.number().int().min(0).optional() })
export type AiArkMonths = z.infer<typeof aiArkMonthsSchema>

export const aiArkKeywordSchema = <S extends readonly [string, ...string[]]>(sources: S) =>
  aiArkTextValuesSchema.extend({ sources: z.array(z.enum(sources)).max(sources.length).default([]) })

export const aiArkAccountKeywordSchema = aiArkKeywordSchema(AI_ARK_ACCOUNT_KEYWORD_SOURCES)
export type AiArkAccountKeyword = z.infer<typeof aiArkAccountKeywordSchema>
export const aiArkContactKeywordSchema = aiArkKeywordSchema(AI_ARK_CONTACT_KEYWORD_SOURCES)
export type AiArkContactKeyword = z.infer<typeof aiArkContactKeywordSchema>

export const aiArkDepartmentMetricSchema = z.object({
  functions: z.array(z.string()).min(1),
  min: z.number().optional(),
  max: z.number().optional(),
})
export const aiArkGrowthMetricSchema = aiArkDepartmentMetricSchema.extend({ timeFrame: z.enum(AI_ARK_GROWTH_TIMEFRAMES) })

/** The company filters (`account`), shared by both tabs. Field names follow the API's where they can. */
export const aiArkAccountFiltersSchema = z.object({
  domain: values.optional(),
  linkedin: values.optional(),
  url: aiArkTextValuesSchema.optional(),
  name: aiArkTextValuesSchema.optional(),
  industries: aiArkTextValuesSchema.optional(),
  /** Any office, not only the HQ. */
  location: aiArkListValuesSchema.optional(),
  geoLocation: z
    .object({ lat: z.number(), lng: z.number(), radius: z.number().positive(), unit: z.enum(['km', 'mi']) })
    .optional(),
  productAndServices: aiArkTextValuesSchema.optional(),
  /** Has a profile on these networks (data only, never fetched). */
  socialMedia: z.array(z.enum(AI_ARK_SOCIAL_NETWORKS)).optional(),
  type: z.object({ include: z.array(z.enum(AI_ARK_COMPANY_TYPES)).optional(), exclude: z.array(z.enum(AI_ARK_COMPANY_TYPES)).optional() }).optional(),
  foundedYear: aiArkRangeSchema.optional(),
  employeeSize: aiArkRangeSchema.optional(),
  /** Number of physical locations (the AI Ark app's "# of Locations World Wide"). */
  retailSize: aiArkRangeSchema.optional(),
  /** USD. Labelled unreliable on the screen: many companies have none. */
  revenue: aiArkRangeSchema.optional(),
  funding: z
    .object({
      types: z.array(z.string()).optional(),
      totalAmount: aiArkRangeBandSchema.optional(),
      lastAmount: aiArkRangeBandSchema.optional(),
      /** ISO dates, the screen's form of the API's epoch-milliseconds range. */
      lastRoundFrom: z.string().optional(),
      lastRoundTo: z.string().optional(),
    })
    .optional(),
  language: values.optional(),
  keyword: aiArkAccountKeywordSchema.optional(),
  metric: z
    .object({ employee: z.array(aiArkDepartmentMetricSchema).optional(), growth: z.array(aiArkGrowthMetricSchema).optional() })
    .optional(),
  technologies: aiArkTextValuesSchema.optional(),
  naics: values.optional(),
})
export type AiArkAccountFilters = z.infer<typeof aiArkAccountFiltersSchema>

/** The person filters (`contact`). */
export const aiArkContactFiltersSchema = z.object({
  fullName: aiArkTextValuesSchema.optional(),
  linkedin: values.optional(),
  socialMediaLink: values.optional(),
  /** Job title, with which role it applies to: the main current one, every current one, or past ones. */
  experience: z
    .object({
      scope: z.enum(['latest', 'current', 'previous']).default('latest'),
      title: aiArkTextValuesSchema.optional(),
      /** Time in the role, at the company and in total. */
      roleMonths: aiArkMonthsSchema.optional(),
      companyMonths: aiArkMonthsSchema.optional(),
      totalMonths: aiArkMonthsSchema.optional(),
    })
    .optional(),
  /** Companies by AI Ark id: the main current one, every current one, past ones. */
  company: z
    .object({ latest: aiArkCompanyRefsSchema.optional(), current: aiArkCompanyRefsSchema.optional(), previous: aiArkCompanyRefsSchema.optional() })
    .optional(),
  seniority: z.object({ include: z.array(z.enum(AI_ARK_SENIORITIES)).optional(), exclude: z.array(z.enum(AI_ARK_SENIORITIES)).optional() }).optional(),
  departmentAndFunction: aiArkListValuesSchema.optional(),
  /** Where the person lives. */
  location: aiArkListValuesSchema.optional(),
  language: aiArkTextValuesSchema.optional(),
  skill: aiArkTextValuesSchema.optional(),
  certification: aiArkTextValuesSchema.optional(),
  education: z
    .object({
      schools: aiArkCompanyRefsSchema.optional(),
      degree: aiArkTextValuesSchema.optional(),
      fieldOfStudy: aiArkTextValuesSchema.optional(),
      from: z.string().optional(),
      to: z.string().optional(),
      present: z.boolean().optional(),
    })
    .optional(),
  keyword: aiArkContactKeywordSchema.optional(),
  socialMedia: z.array(z.enum(AI_ARK_SOCIAL_NETWORKS)).optional(),
  profileBadge: z.array(z.enum(AI_ARK_PROFILE_BADGES)).optional(),
  /** LinkedIn only: the API answers 501 for any other network. */
  linkedinFollowers: aiArkRangeSchema.optional(),
  linkedinConnections: aiArkRangeSchema.optional(),
})
export type AiArkContactFilters = z.infer<typeof aiArkContactFiltersSchema>

/** Company Search only: companies by the people they employ. */
export const aiArkEmployeeFiltersSchema = z.object({
  title: aiArkTextValuesSchema.optional(),
  seniority: z.array(z.enum(AI_ARK_SENIORITIES)).optional(),
  departmentAndFunction: values.optional(),
})
export type AiArkEmployeeFilters = z.infer<typeof aiArkEmployeeFiltersSchema>

/** What the people tab edits. */
export const aiArkPeopleFiltersSchema = z.object({
  contact: aiArkContactFiltersSchema.default({}),
  account: aiArkAccountFiltersSchema.default({}),
  /** Leave out everyone this brand already imported from AI Ark (an exclusion List built per run). */
  netNewOnly: z.boolean().default(false),
  /** AI Ark List ids to exclude (at most 10 per request, the API's limit). */
  excludeListIds: z.array(z.string()).max(10).default([]),
})
export type AiArkPeopleFilters = z.infer<typeof aiArkPeopleFiltersSchema>

/** What the company tab edits. */
export const aiArkCompanyFiltersSchema = z.object({
  account: aiArkAccountFiltersSchema.default({}),
  employee: aiArkEmployeeFiltersSchema.optional(),
  /** Up to 5 domains or LinkedIn company URLs ("e.g. Nike, nike.com, linkedin.com/company/nike"). */
  lookalikeDomains: z.array(z.string().min(1)).max(5).default([]),
  excludeListIds: z.array(z.string()).max(10).default([]),
})
export type AiArkCompanyFilters = z.infer<typeof aiArkCompanyFiltersSchema>

export const emptyAiArkPeopleFilters = (): AiArkPeopleFilters => ({ contact: {}, account: {}, netNewOnly: false, excludeListIds: [] })
export const emptyAiArkCompanyFilters = (): AiArkCompanyFilters => ({ account: {}, lookalikeDomains: [], excludeListIds: [] })

/**
 * An API body as AI Ark takes it (`account`, `contact`, `lists`, `page`,
 * `size`, and on Company Search `lookalikeDomains`). Loosely typed on
 * purpose: filters.ts owns the exact shape, and a pasted payload may carry
 * fields this model does not edit, which the parser reports as unsupported.
 */
export interface AiArkApiBody {
  account?: Record<string, unknown>
  contact?: Record<string, unknown>
  lists?: Record<string, unknown>
  lookalikeDomains?: string[]
  page?: number
  size?: number
  [key: string]: unknown
}

/* ------------------------------------------------------------------ */
/* Results                                                             */
/* ------------------------------------------------------------------ */

/** What data AI Ark holds for a person, from People Preview's `has_*` flags. */
export interface AiArkHasFlags {
  mobile: boolean
  skills: boolean
  educations: boolean
  languages: boolean
  certifications: boolean
  memberBadges: boolean
  awards: boolean
  patents: boolean
  publications: boolean
  projects: boolean
  courses: boolean
  organizations: boolean
  volunteerExperiences: boolean
  testScores: boolean
}

/** One row of the people results (People Preview: the last name stays masked until the person is added). */
export interface AiArkPersonPreview {
  /** AI Ark person id. Export Single and Lists take it. */
  id: string
  firstName: string
  /** As AI Ark masks it, for example "Te***n". */
  lastNameMasked: string
  title: string | null
  headline: string | null
  seniority: string | null
  departments: string[]
  location: { city: string | null; state: string | null; country: string | null; label: string | null }
  company: {
    id: string | null
    name: string | null
    industry: string | null
    staffTotal: number | null
    staffRange: { min: number | null; max: number | null } | null
    foundedYear: number | null
    type: string | null
    location: string | null
  }
  has: AiArkHasFlags
  /** True when this brand already imported this person (ai_ark_people), so the In Contacts tab can count them. */
  inContacts: boolean
  prospectId: string | null
  lastUpdated: string | null
}

/** One row of the company results. */
export interface AiArkCompanySummary {
  id: string
  name: string
  domain: string | null
  website: string | null
  /** Stored as data and never fetched. */
  linkedin: string | null
  logo: string | null
  description: string | null
  industry: string | null
  industries: string[]
  type: string | null
  foundedYear: number | null
  staffTotal: number | null
  staffRange: { min: number | null; max: number | null } | null
  headquarters: string | null
  locationsCount: number
  revenueRange: string | null
  fundingType: string | null
  fundingTotal: number | null
  technologies: string[]
  keywords: string[]
  lastUpdated: string | null
}

/* ------------------------------------------------------------------ */
/* Error codes                                                         */
/* ------------------------------------------------------------------ */

/**
 * Machine-readable codes. The API answers `{ error: { code, message } }`
 * with these, and the screen maps each to words (no code in what a person reads).
 */
export const AI_ARK_ERROR_CODES = [
  /** ARK_AI_API_KEY is not set on this server. */
  'ai_ark_key_missing',
  /** AI Ark answered 401. */
  'ai_ark_key_invalid',
  /** AI Ark answered 402. */
  'ai_ark_out_of_credits',
  /** The `ai_ark` settings row has `enabled` off. */
  'ai_ark_disabled',
  /** A feature switch in the settings row is off (company search, lookalike, ...). */
  'ai_ark_feature_off',
  /** The filters cannot be sent (excludes alone, too many values, an unknown field). */
  'ai_ark_filter_invalid',
  /** AI Ark answered 501 (followers on a network other than LinkedIn). */
  'ai_ark_filter_unsupported',
  /** AI Ark answered 400 for another reason (500 pending jobs, list limits). */
  'ai_ark_bad_request',
  /** AI Ark answered 429 and the retries ran out. */
  'ai_ark_rate_limited',
  /** AI Ark answered 5xx or did not answer, and the retries ran out. */
  'ai_ark_unavailable',
  /** The worst case would cross `budget.maxCreditsPerRun`. */
  'ai_ark_over_run_budget',
  /** The worst case plus today's spend would cross `budget.maxCreditsPerDay`. */
  'ai_ark_over_day_budget',
  /** The balance after the worst case would fall under `budget.minBalanceFloor`. */
  'ai_ark_under_floor',
  /** The balance could not be read, so nothing paid is started. */
  'ai_ark_balance_unknown',
  /** POST /exports carried an estimate that no longer matches (the search or the prices moved). */
  'ai_ark_estimate_moved',
  /** POST /exports without `confirm: true`. */
  'ai_ark_not_confirmed',
  /** The size asked is over `export.maxPerJob` times the jobs a run may make, or under 1. */
  'ai_ark_size_invalid',
  /** The export, job or saved search names nothing in this brand. */
  'ai_ark_not_found',
  /** A track id answered 403: refunded for non-delivery, resubmit. */
  'ai_ark_refunded',
  /** A track id answered 409: results not ready yet. */
  'ai_ark_not_ready',
  /** A run is already running for this brand (one at a time). */
  'ai_ark_run_busy',
  /** The pasted payload is not an AI Ark search body. */
  'ai_ark_payload_invalid',
] as const
export type AiArkErrorCode = (typeof AI_ARK_ERROR_CODES)[number]

/** Why a paid call was refused before it was made. A subset of the error codes. */
export type AiArkRefusal =
  | 'ai_ark_disabled'
  | 'ai_ark_key_missing'
  | 'ai_ark_over_run_budget'
  | 'ai_ark_over_day_budget'
  | 'ai_ark_under_floor'
  | 'ai_ark_balance_unknown'
  | 'ai_ark_size_invalid'
  | 'ai_ark_feature_off'

export interface AiArkErrorBody {
  error: { code: AiArkErrorCode; message: string }
}

/* ------------------------------------------------------------------ */
/* Export runs and jobs                                                */
/* ------------------------------------------------------------------ */

/**
 * A run is what a person started with Add to prospects (`ai_ark_exports`).
 * queued     written and enqueued on the ai-ark-export queue
 * running    exporting: jobs are being submitted, polled or paged
 * importing  every job's people are stored and the import into Contacts runs
 * imported   done: the list exists in Contacts
 * refunded   AI Ark refunded a job and nothing was imported for it, Resubmit offered
 * failed     stopped with an error (the reason is on the row)
 */
export const AI_ARK_RUN_STATES = ['queued', 'running', 'importing', 'imported', 'refunded', 'failed'] as const
export type AiArkRunState = (typeof AI_ARK_RUN_STATES)[number]

/**
 * One AI Ark export job of at most `export.maxPerJob` people (`ai_ark_export_jobs`).
 * pending    written, or submitted and waiting for AI Ark (track id set once submitted)
 * done       AI Ark says DONE, results not yet stored
 * importing  results stored in ai_ark_people, import running
 * imported   its people are in Contacts
 * refunded   AI Ark answered 403 on the track id (STUCK_HARD or STUCK_SOFT refund)
 * failed     stopped with an error
 */
export const AI_ARK_JOB_STATES = ['pending', 'done', 'importing', 'imported', 'refunded', 'failed'] as const
export type AiArkJobState = (typeof AI_ARK_JOB_STATES)[number]

/** Everyone who matches up to N (Export People with Email), or only the picked rows (Export Single per person). */
export const AI_ARK_EXPORT_MODES = ['bulk', 'picked'] as const
export type AiArkExportMode = (typeof AI_ARK_EXPORT_MODES)[number]

export interface AiArkRunCounts {
  /** People AI Ark exported (paid at the per-person price). */
  exported: number
  /** Valid emails found (paid at the per-email price). Catch-all addresses count, as AI Ark charges for them. */
  emailsFound: number
  /** Of those, on a catch-all domain (imported as `catch_all`, not mailable under today's gate). */
  catchAll: number
  imported: number
  /** Already in Contacts: matched, kept on their old list. */
  duplicates: number
  /** Dropped at ingestion because they are suppressed. */
  suppressed: number
}

export interface AiArkExportJob {
  id: string
  chunk: number
  trackId: string | null
  state: AiArkJobState
  sizeRequested: number
  /** AI Ark's own `statistics` (total, found). */
  total: number | null
  found: number | null
  creditsEstimated: number
  creditsCharged: number | null
  refundReason: string | null
  submittedAt: string | null
  doneAt: string | null
  error: string | null
}

export interface AiArkExportRunSummary {
  id: string
  brandId: string
  searchId: string | null
  searchName: string | null
  mode: AiArkExportMode
  state: AiArkRunState
  sizeRequested: number
  listName: string
  /** The Contacts list (an import batch). A campaign for it uses this id as `importBatchId`. */
  importBatchId: string | null
  /** The draft campaign made from this list, when one was (a `campaigns` row, the playbook routes' id). */
  campaignId: string | null
  counts: AiArkRunCounts
  creditsEstimated: number
  creditsCharged: number | null
  createdBy: string | null
  createdAt: string
  finishedAt: string | null
  error: string | null
}

export interface AiArkExportRun extends AiArkExportRunSummary {
  jobs: AiArkExportJob[]
  filters: AiArkPeopleFilters | null
  personIds: string[]
  balanceBefore: number | null
  balanceAfter: number | null
}

/* ------------------------------------------------------------------ */
/* Saved searches                                                      */
/* ------------------------------------------------------------------ */

export interface AiArkSavedSearch {
  id: string
  brandId: string
  name: string
  kind: AiArkSearchKind
  filters: AiArkPeopleFilters | AiArkCompanyFilters
  apiBody: AiArkApiBody
  createdBy: string | null
  createdAt: string
  updatedAt: string
  lastRunAt: string | null
  lastTotal: number | null
}

/* ------------------------------------------------------------------ */
/* Routes: requests (zod, for parseBody) and answers                   */
/* ------------------------------------------------------------------ */

/** GET /ai-ark/credits. Free. */
export interface AiArkCreditsAnswer {
  enabled: boolean
  keyConfigured: boolean
  /** AI Ark's balance, read live (free). Null when it could not be read. */
  balance: number | null
  balanceReadAt: string | null
  spentToday: number
  spentThisMonth: number
  floor: number
  maxCreditsPerRun: number
  maxCreditsPerDay: number
  /** What today's limit and the floor still allow, the smaller of the two. */
  headroom: number | null
  prices: AiArkPrices
  /** The list name prefix and the preview page size in force, so the screens need not fall back to the defaults. */
  export: { defaultListPrefix: string }
  preview: { pageSize: number }
}

/** GET /ai-ark/vocab/:kind?q=. Free, from the vendored CSVs. */
export const aiArkVocabParamsSchema = z.object({ kind: z.enum(AI_ARK_VOCAB_KINDS) })
export const aiArkVocabQuerySchema = z.object({ q: z.string().max(100).default(''), limit: z.coerce.number().int().min(1).max(100).default(25) })
export interface AiArkVocabAnswer {
  kind: AiArkVocabKind
  q: string
  values: { value: string; label: string; companyCount: number | null }[]
}

/** POST /ai-ark/people/preview. 1 credit a page. */
export const aiArkPeoplePreviewRequestSchema = z.object({
  filters: aiArkPeopleFiltersSchema,
  page: z.number().int().min(0).default(0),
  /** Defaults to `preview.pageSize`. AI Ark takes 25 to 100. */
  size: z.number().int().min(1).max(100).optional(),
  searchId: z.string().uuid().optional(),
})
export type AiArkPeoplePreviewRequest = z.infer<typeof aiArkPeoplePreviewRequestSchema>
export interface AiArkPeoplePreviewAnswer {
  total: number
  page: number
  size: number
  totalPages: number
  people: AiArkPersonPreview[]
  /** People on this page already in Contacts. */
  pageInContacts: number
  creditsCharged: number
}

/** POST /ai-ark/companies/search. 0.1 a company. */
export const aiArkCompanySearchRequestSchema = z.object({
  filters: aiArkCompanyFiltersSchema,
  page: z.number().int().min(0).default(0),
  size: z.number().int().min(1).max(100).optional(),
  searchId: z.string().uuid().optional(),
})
export type AiArkCompanySearchRequest = z.infer<typeof aiArkCompanySearchRequestSchema>
export interface AiArkCompanySearchAnswer {
  total: number
  page: number
  size: number
  totalPages: number
  companies: AiArkCompanySummary[]
  creditsCharged: number
}

/** The source of an export: a saved search, filters, or picked person ids. */
const exportSource = {
  searchId: z.string().uuid().optional(),
  filters: aiArkPeopleFiltersSchema.optional(),
  mode: z.enum(AI_ARK_EXPORT_MODES).default('bulk'),
  /** Picked rows (mode `picked`): AI Ark person ids. */
  personIds: z.array(z.string().min(1)).max(10000).optional(),
  size: z.number().int().min(1),
}

/** POST /ai-ark/estimate. Free. */
export const aiArkEstimateRequestSchema = z.object(exportSource)
export type AiArkEstimateRequest = z.infer<typeof aiArkEstimateRequestSchema>
export interface AiArkEstimate {
  mode: AiArkExportMode
  /** People the export would ask for: the smaller of `size` and the match count. */
  people: number
  /** How many export jobs that makes. */
  jobs: number
  /** The worst case: every person with an email found. */
  worstCaseCredits: number
  balance: number | null
  balanceAfter: number | null
  floor: number
  spentToday: number
  maxCreditsPerRun: number
  maxCreditsPerDay: number
  allowed: boolean
  refusal: AiArkRefusal | null
}

/** POST /ai-ark/exports. Starts a run. Refused if the estimate moved or a limit would be crossed. */
export const aiArkExportRequestSchema = z.object({
  ...exportSource,
  listName: z.string().trim().min(1).max(200).optional(),
  confirm: z.literal(true),
  /** `worstCaseCredits` from the estimate the person saw. */
  estimate: z.number().min(0),
})
export type AiArkExportRequest = z.infer<typeof aiArkExportRequestSchema>
export interface AiArkExportAnswer {
  run: AiArkExportRun
}

/** GET /ai-ark/exports and GET /ai-ark/exports/:id. Free. */
export const aiArkExportParamsSchema = z.object({ id: z.string().uuid() })
/** PUT /ai-ark/exports/:id/campaign: the draft campaign started for the run's list, so its row offers Open the campaign. */
export const aiArkExportCampaignSchema = z.object({ campaignId: z.string().uuid() }).strict()
export type AiArkExportCampaign = z.infer<typeof aiArkExportCampaignSchema>
export interface AiArkExportListAnswer {
  runs: AiArkExportRunSummary[]
}
export interface AiArkExportDetailAnswer {
  run: AiArkExportRun
}

/** POST /ai-ark/exports/:id/resubmit. Resubmits the refunded jobs, priced as an export. */
export const aiArkResubmitRequestSchema = z.object({ confirm: z.literal(true), estimate: z.number().min(0) })
export type AiArkResubmitRequest = z.infer<typeof aiArkResubmitRequestSchema>

/** POST /ai-ark/hook/:token. No session. The body is never read. */
export const aiArkHookParamsSchema = z.object({ token: z.string().regex(/^[A-Za-z0-9_-]{32,128}$/) })

/** Saved searches: GET, POST, PUT, DELETE /ai-ark/searches. Free. */
export const aiArkSearchParamsSchema = z.object({ id: z.string().uuid() })
export const aiArkSearchCreateSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('people'), name: z.string().trim().min(1).max(200), filters: aiArkPeopleFiltersSchema }),
  z.object({ kind: z.literal('company'), name: z.string().trim().min(1).max(200), filters: aiArkCompanyFiltersSchema }),
])
export type AiArkSearchCreate = z.infer<typeof aiArkSearchCreateSchema>
export const aiArkSearchUpdateSchema = z.object({
  name: z.string().trim().min(1).max(200).optional(),
  filters: z.union([aiArkPeopleFiltersSchema, aiArkCompanyFiltersSchema]).optional(),
})
export type AiArkSearchUpdate = z.infer<typeof aiArkSearchUpdateSchema>
export interface AiArkSearchListAnswer {
  searches: AiArkSavedSearch[]
}
export interface AiArkSearchAnswer {
  search: AiArkSavedSearch
}

/** POST /ai-ark/payload/parse. A search copied from the AI Ark app's Search Payload panel. Free. */
export const aiArkPayloadParseRequestSchema = z.object({
  /** The pasted JSON, as text or already parsed. */
  payload: z.union([z.string().min(2).max(200_000), z.record(z.unknown())]),
  /** People or company, when the person knows. Left out, it is worked out from the URL and the fields. */
  kind: z.enum(['people', 'company']).optional(),
})
export type AiArkPayloadParseRequest = z.infer<typeof aiArkPayloadParseRequestSchema>
export type AiArkPayloadParseAnswer =
  | { kind: 'people'; filters: AiArkPeopleFilters; unsupported: string[]; warnings: string[] }
  | { kind: 'company'; filters: AiArkCompanyFilters; unsupported: string[]; warnings: string[] }

/* ------------------------------------------------------------------ */
/* Settings (one safety_settings row, key ai_ark)                      */
/* ------------------------------------------------------------------ */

export const AI_ARK_SETTINGS_KEY = 'ai_ark'

const credits = z.number().min(0)

export const aiArkPricesSchema = z.object({
  /** People Preview, flat per page whatever its size. */
  previewPerPage: credits,
  /** People Search, per person returned. */
  peopleSearchPerResult: credits,
  /** Company Search, per company returned. */
  companySearchPerResult: credits,
  /** Export People with Email, per person exported, found or not. */
  exportPerPerson: credits,
  /** Export People with Email, per valid email found (catch-all included). */
  exportPerEmail: credits,
  /** Export Single, per person with a valid email (0 without). */
  exportSinglePerEmail: credits,
  /** Reverse People Lookup, per request. */
  reverseLookupPerRequest: credits,
})
export type AiArkPrices = z.infer<typeof aiArkPricesSchema>

/**
 * The reasons writer for AI Ark lists (owner queue item 11), under the
 * `reasons` key of the same row. Off until the owner turns it on.
 */
export const aiArkReasonsSettingsSchema = z.object({
  /** The worker pass. Off: nothing writes reasons for AI Ark lists on its own, and the bulk button still works. */
  workerPass: z.boolean(),
  /** People one job takes before it hands back. A job is one brand. */
  peoplePerJob: z.number().int().min(1).max(10_000),
  /** Reasons written at the same time inside one job. */
  concurrency: z.number().int().min(1).max(32),
  /** A job stops starting new people after this long, so a deploy loses little and the queue moves. */
  maxSecondsPerJob: z.number().int().min(10).max(3600),
  /** Reasons the pass may write for one brand in one UTC day. 0 writes none. */
  maxPeoplePerDay: z.number().int().min(0),
})
export type AiArkReasonsSettings = z.infer<typeof aiArkReasonsSettingsSchema>

/**
 * The defaults. The pass is off. When it is on, eight writers at about 10
 * seconds a person (measured 2026-10-31 on the deep tier) write about 2,800 an
 * hour, and the day's cap keeps a brand to 20,000 a day unless the owner raises it.
 * Migration 0221 seeds exactly this under the row's `reasons` key.
 */
export const AI_ARK_REASONS_SETTINGS_DEFAULT: AiArkReasonsSettings = {
  workerPass: false,
  peoplePerJob: 500,
  concurrency: 8,
  maxSecondsPerJob: 600,
  maxPeoplePerDay: 20_000,
}

export const aiArkSettingsSchema = z.object({
  /** Off hides the tab and refuses every paid route, without a deploy. */
  enabled: z.boolean(),
  visibility: z.object({ adminsOnly: z.boolean() }),
  prices: aiArkPricesSchema,
  budget: z.object({
    maxCreditsPerRun: credits,
    maxCreditsPerDay: credits,
    /** No run may take AI Ark's shared balance under this (BetterEnrich draws on it too). */
    minBalanceFloor: credits,
  }),
  export: z.object({
    /** AI Ark's limit per export job. */
    maxPerJob: z.number().int().min(1).max(10000),
    /** The most jobs one run may make (a run past maxPerJob excludes each job's people from the next). */
    maxJobsPerRun: z.number().int().min(1),
    defaultListPrefix: z.string().min(1),
    mode: z.enum(AI_ARK_EXPORT_MODES),
    /** The webhook AI Ark calls when a job is done, the hook token appended. HTTPS, the production path. */
    webhookBase: z.string().url(),
    pollEverySeconds: z.number().int().min(1),
    /** A job still pending after this long is marked failed and the submission history is read. */
    giveUpAfterHours: z.number().min(1),
    /** `/inquiries` page size (AI Ark's maximum is 100). */
    inquiriesPageSize: z.number().int().min(1).max(100),
    /** Exclusion Lists per request (AI Ark's limit is 10) and values per List (its limit is 10,000). */
    exclusionListsMax: z.number().int().min(0).max(10),
    exclusionListSize: z.number().int().min(1).max(10000),
  }),
  preview: z.object({ pageSize: z.number().int().min(25).max(100) }),
  companySearch: z.object({ pageSize: z.number().int().min(1).max(100) }),
  rate: z.object({
    requestsPerSecond: z.number().min(0.1),
    maxRetries: z.number().int().min(0),
    backoffBaseMs: z.number().int().min(0),
    timeoutMs: z.number().int().min(1000),
  }),
  sweeper: z.object({ everySeconds: z.number().int().min(10) }),
  features: z.object({
    companySearch: z.boolean(),
    lookalike: z.boolean(),
    reverseLookup: z.boolean(),
    enrichContacts: z.boolean(),
    phone: z.boolean(),
    personality: z.boolean(),
    aiFilterWriter: z.boolean(),
  }),
  /**
   * The reasons writer (read in full by resolveAiArkReasonsSettings in the
   * intel package, each field over AI_ARK_REASONS_SETTINGS_DEFAULT). Kept
   * here so a save from Settings does not strip it.
   */
  reasons: aiArkReasonsSettingsSchema.partial().optional(),
})
export type AiArkSettings = z.infer<typeof aiArkSettingsSchema>

/** PUT /ai-ark/settings: a part of the row, merged over the stored one. */
export const aiArkSettingsUpdateSchema = z.object({ settings: z.record(z.unknown()) }).strict()
export type AiArkSettingsUpdate = z.infer<typeof aiArkSettingsUpdateSchema>

/**
 * The defaults (handover section 4). Migration 0220 seeds exactly this, and a
 * test in packages/intel/src/ai-ark/ checks the two never drift apart.
 */
export const AI_ARK_SETTINGS_DEFAULT: AiArkSettings = {
  enabled: true,
  visibility: { adminsOnly: true },
  prices: {
    previewPerPage: 1,
    peopleSearchPerResult: 0.5,
    companySearchPerResult: 0.1,
    exportPerPerson: 0.5,
    exportPerEmail: 0.5,
    exportSinglePerEmail: 1,
    reverseLookupPerRequest: 0.5,
  },
  budget: { maxCreditsPerRun: 20000, maxCreditsPerDay: 60000, minBalanceFloor: 100000 },
  export: {
    maxPerJob: 10000,
    maxJobsPerRun: 10,
    defaultListPrefix: 'AI Ark',
    mode: 'bulk',
    webhookBase: 'https://sonofbasel.firmadapt.com/api/v1/ai-ark/hook/',
    pollEverySeconds: 10,
    giveUpAfterHours: 12,
    inquiriesPageSize: 100,
    exclusionListsMax: 10,
    exclusionListSize: 10000,
  },
  preview: { pageSize: 25 },
  companySearch: { pageSize: 10 },
  rate: { requestsPerSecond: 5, maxRetries: 4, backoffBaseMs: 500, timeoutMs: 30000 },
  sweeper: { everySeconds: 60 },
  features: {
    companySearch: true,
    lookalike: true,
    reverseLookup: false,
    enrichContacts: false,
    phone: false,
    personality: false,
    aiFilterWriter: false,
  },
}

/** Settings > AI Ark: GET answers the row, PUT takes a partial row (merged over the stored one, then validated). */
export interface AiArkSettingsAnswer {
  settings: AiArkSettings
  defaults: AiArkSettings
}

/**
 * Fill a stored row's gaps from the default, then validate. The same rule as
 * the Operator's settings: a field a stored row lacks takes the default, a
 * field it has must be valid.
 */
export function parseAiArkSettings(stored: unknown): AiArkSettings {
  return aiArkSettingsSchema.parse(mergeOver(AI_ARK_SETTINGS_DEFAULT, stored))
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function mergeOver<T>(base: T, over: unknown): T {
  if (!isPlainObject(base) || !isPlainObject(over)) return (over === undefined || over === null ? base : over) as T
  const out: Record<string, unknown> = { ...base }
  for (const [key, value] of Object.entries(over)) {
    out[key] = key in base ? mergeOver((base as Record<string, unknown>)[key], value) : value
  }
  return out as T
}

/* ------------------------------------------------------------------ */
/* Paths                                                               */
/* ------------------------------------------------------------------ */

/** The screens (apps/web/src/routes.tsx). */
export const AI_ARK_PATHS = {
  search: '/discovery/ai-ark',
  exports: '/discovery/ai-ark/exports',
  settings: '/settings/ai-ark',
} as const

/** The default list name: "AI Ark <search name> <date>". */
export function aiArkDefaultListName(prefix: string, searchName: string | null, date: Date): string {
  const day = date.toISOString().slice(0, 10)
  return [prefix, searchName?.trim() || null, day].filter(Boolean).join(' ')
}
